import { useState, useMemo, useRef, useLayoutEffect, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';
import PoolDetailPanel from './PoolDetailPanel';
import MineShaftSelector from './MineShaftSelector';
import FloatingPoolButton from './FloatingPoolButton';
import './PoolsMainView.scss';
import './PumpsMainView.scss';

const SCROLL_STORAGE_KEY = 'poolsListScrollPosition';
const SEARCH_STORAGE_KEY = 'poolsListSearchTerm';
const MINE_FILTER_KEY = 'poolsFilterSelectedMine';
const SHAFT_FILTER_KEY = 'poolsFilterSelectedShaft';

const POOLS_FETCHED_KEY = 'poolsFetchedInSession';

export default function PoolsMainView() {
  const { t } = useTranslation();
  const { poolId } = useParams();
  const navigate = useNavigate();
  const listScrollRef = useRef(null);
  
  // Get pools from store instead of props
  const { pools, loading } = useStoreState((state) => state.pools);
  const { getPools } = useStoreActions((actions) => actions.pools);
  
  // Fetch pools when component mounts, but only if not already loaded
  useEffect(() => {
    const poolsFetched = sessionStorage.getItem(POOLS_FETCHED_KEY);
    
    // Only fetch if:
    // 1. Haven't been fetched in this session AND
    // 2. Store is empty AND
    // 3. Not currently loading
    if (!poolsFetched && pools.length === 0 && !loading) {
      console.log('PoolsMainView: Fetching pools (first time in session)');
      getPools()
        .then(() => {
          sessionStorage.setItem(POOLS_FETCHED_KEY, 'true');
          console.log('PoolsMainView: Pools fetched and marked in sessionStorage');
        })
        .catch(error => {
          console.error('PoolsMainView: Error fetching pools:', error);
        });
    } else {
      console.log('PoolsMainView: Pools already fetched or loaded, skipping fetch. poolsFetched:', poolsFetched, 'pools.length:', pools.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fetch pools once on mount
  }, []);
  
  console.log('PoolsMainView render - poolId:', poolId);
  
  // Initialize filters from sessionStorage
  const [selectedMine, setSelectedMine] = useState(() => {
    const saved = sessionStorage.getItem(MINE_FILTER_KEY);
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedShaft, setSelectedShaft] = useState(() => {
    const saved = sessionStorage.getItem(SHAFT_FILTER_KEY);
    return saved ? JSON.parse(saved) : null;
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem(SEARCH_STORAGE_KEY) || '';
  });

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    sessionStorage.setItem(SEARCH_STORAGE_KEY, value);
  };

  // Save scroll position on scroll
  const handleListScroll = (e) => {
    const position = e.target.scrollTop;
    sessionStorage.setItem(SCROLL_STORAGE_KEY, position.toString());
  };

  // Restore scroll position after poolId changes
  useLayoutEffect(() => {
    const container = listScrollRef.current;
    const savedPosition = parseInt(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '0', 10);
    
    if (container && savedPosition > 0) {
      container.scrollTop = savedPosition;
      
      setTimeout(() => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      }, 0);
      
      const timeoutId = setTimeout(() => {
        if (container) {
          container.scrollTop = savedPosition;
        }
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [poolId]);

  // Filter pools by mine and shaft, then by search (name, shaft, mine)
  const mineShaftFilteredPools = useMemo(() => {
    if (!pools) return [];

    let filtered = pools;

    if (selectedMine) {
      filtered = filtered.filter((pool) => pool.shaft?.mine?.id === selectedMine.id);
    }

    if (selectedShaft) {
      filtered = filtered.filter((pool) => pool.shaft?.id === selectedShaft.id);
    }

    return filtered;
  }, [pools, selectedMine, selectedShaft]);

  const filteredPools = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    if (!searchLower) return mineShaftFilteredPools;

    return mineShaftFilteredPools.filter((pool) => {
      const name = pool.name?.toLowerCase() ?? '';
      const shaftName = pool.shaft?.name?.toLowerCase() ?? '';
      const mineName = pool.shaft?.mine?.name?.toLowerCase() ?? '';
      return (
        name.includes(searchLower) ||
        shaftName.includes(searchLower) ||
        mineName.includes(searchLower)
      );
    });
  }, [mineShaftFilteredPools, searchTerm]);

  const handleMineSelect = (mine) => {
    setSelectedMine(mine);
    // Save to sessionStorage
    if (mine) {
      sessionStorage.setItem(MINE_FILTER_KEY, JSON.stringify(mine));
    } else {
      sessionStorage.removeItem(MINE_FILTER_KEY);
    }
  };

  const handleShaftSelect = (shaft) => {
    setSelectedShaft(shaft);
    // Save to sessionStorage
    if (shaft) {
      sessionStorage.setItem(SHAFT_FILTER_KEY, JSON.stringify(shaft));
    } else {
      sessionStorage.removeItem(SHAFT_FILTER_KEY);
    }
  };

  const handlePoolClick = (pool) => {
    navigate(`/dashboard/pools/${pool.id}`);
  };

  const handleBackToList = () => {
    navigate('/dashboard/pools');
  };

  // Pool API returns is_active; a missing value is treated as active.
  const isPoolActive = (pool) => pool?.is_active !== false;

  const getStatusDisplay = (pool) =>
    isPoolActive(pool) ? t('pool_status_active') : t('pool_status_inactive');

  const getStatusColor = (pool) => (isPoolActive(pool) ? 'status-green' : 'status-red');

  return (
    <div className={`pools-main-view ${poolId ? 'has-pool-selected' : ''}`}>
      <div className="dashboard-toolbar pools-toolbar d-flex align-items-center flex-nowrap justify-content-between gap-2">
        <MineShaftSelector
          onMineSelect={handleMineSelect}
          onShaftSelect={handleShaftSelect}
          selectedMineId={selectedMine?.id}
          selectedShaftId={selectedShaft?.id}
        />
        <div className="pump-search-wrapper toolbar-search-end">
          <i className="fas fa-search pump-search-icon" />
          <input
            type="text"
            className="pump-search-input"
            placeholder={t('pool_search_placeholder')}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchTerm && (
            <button
              className="pump-search-clear"
              onClick={() => handleSearchChange('')}
              type="button"
            >
              ×
            </button>
          )}
        </div>
      </div>
      
      <div className="pools-master-detail-container">
        {/* Master - Pool List */}
        <div className={`pools-master ${poolId ? 'has-selection' : ''}`}>
          <div ref={listScrollRef} onScroll={handleListScroll} className="pools-list-container">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">{t('ui_loading')}</span>
                </div>
                <p className="mt-2">{t('pool_list_loading')}</p>
              </div>
            ) : filteredPools.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">
                  {mineShaftFilteredPools.length === 0
                    ? (selectedMine || selectedShaft)
                      ? t('pool_list_empty_filters')
                      : t('pool_list_empty_none')
                    : t('pool_list_empty_search')}
                </p>
              </div>
            ) : (
              <div className="pool-list">
                {filteredPools.map((pool, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handlePoolClick(pool)} 
                    style={{ cursor: 'pointer' }}
                    className={poolId && pool.id === parseInt(poolId) ? 'pool-card-selected' : ''}
                  >
                    <div className="pool-card d-flex justify-content-between align-items-center p-3 border-bottom">
                      <div>
                        <div className="pool-name font-weight-bold">{pool.name}</div>
                        <div className="pool-details text-muted small">
                          {pool.shaft?.mine?.name || t('pool_list_no_mine')} | {pool.shaft?.name || t('pool_list_no_shaft')}
                        </div>
                      </div>
                      <div className={`status-badge ${getStatusColor(pool)}`}>{getStatusDisplay(pool)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail - Pool Details */}
        <div className={`pools-detail ${poolId ? 'show-detail' : ''}`}>
          <div className="panel-wrapper">
            <PoolDetailPanel
              key={poolId || 'no-pool'}
              poolId={poolId}
              onBack={handleBackToList}
            />
          </div>
        </div>
      </div>
      
      <FloatingPoolButton />
    </div>
  );
}

